import React, { ReactElement, useEffect, useState } from "react";
import { useLocale } from "@davi-ai/retorik-framework";
import translation from "../translations/menuItems.json";
import Title from "./Settings/Title";
import LeftArrowIcon from "./icons/LeftArrowIcon";
import CloseIcon from "./icons/CloseIcon";
import BackgroundChoice from "./Settings/BackgroundChoice";
import ChatbotDisplay from "./Settings/ChatbotDisplay";
import Location from "./Settings/Location";
import RightChevronIcon from "./icons/RightChevronIcon";
import FontSize from "./Settings/FontSize";
import SkipWelcome from "./Settings/SkipWelcome";
import VeilleChoice from "./Settings/VeilleChoice";
import InformationChannel from "./Settings/InfoChannel";
import LanguageSettings from "./Settings/LanguageSettings";
import DefaultModeChoice from "./Settings/DefaultModeChoice";
import PrintingOptions from "./Settings/PrintingOptions";
import { useConfiguration } from "../redux/Hooks";

interface Action {
  label: string;
  component: ReactElement;
};

const SettingsScreen = (props: { onClose: any }) => {
  const locale: string = useLocale();
  const configuration = useConfiguration();
  const [action, setAction] = useState<Action | undefined>(undefined);
  const [isVertical, setIsVertical] = useState<boolean>(
    window.innerHeight > window.innerWidth
  );

  useEffect(() => {
    document.addEventListener("resize", handleResize);

    return () => {
      document.removeEventListener("resize", handleResize);
    };
  }, []);

  function handleResize(e: Event): void {
    setIsVertical(window.innerHeight > window.innerWidth);
  }

  const disableSpeechAction = !configuration.disableSpeechMode ? [{
    label:
      translation[locale]?.defaultmode.title ||
      translation["fr-FR"].defaultmode.title,
    component: <DefaultModeChoice />,
  }] : [];

  const actions: Array<Action> = [
    ...disableSpeechAction,
    {
      label: translation[locale]?.background || translation["fr-FR"].background,
      component: <BackgroundChoice from={"home"} />,
    },
    {
      label: translation[locale]?.chatbot || translation["fr-FR"].chatbot,
      component: <ChatbotDisplay />,
    },
    {
      label: translation[locale]?.location || translation["fr-FR"].location,
      component: <Location />,
    },
    {
      label: translation[locale]?.font || translation["fr-FR"].font,
      component: <FontSize />,
    },
    {
      label: translation[locale]?.language || translation["fr-FR"].language,
      component: <LanguageSettings />,
    },
    {
      label: translation[locale]?.welcome || translation["fr-FR"].welcome,
      component: <SkipWelcome />,
    },
    {
      label: translation[locale]?.veille || translation["fr-FR"].veille,
      component: <VeilleChoice />,
    },
    {
      label: translation[locale]?.news || translation["fr-FR"].news,
      component: <InformationChannel />,
    },
    {
      label: translation[locale]?.print || translation["fr-FR"].print,
      component: <PrintingOptions />,
    },
  ];

  return (
    <div
      className={`h-full z-50 ${
        isVertical ? "col-start-3 col-span-4" : "col-start-5 col-span-3"
      } flex flex-col justify-center items-center shadow-[3px_3px_6px_#00000029] overflow-y-scroll scrollbar-hidden`}
      style={{
        background: "linear-gradient(to right bottom, #F8F8FC , #E1E2E8)",
      }}
    >
      <div className="w-full grid grid-cols-8 h-1/10 items-stretch bg-black/20">
        {action && (
          <div
            className="col-span-7 pl-2 flex items-center gap-4 uppercase hover:cursor-pointer"
            onClick={() => setAction(undefined)}
          >
            <LeftArrowIcon />
            Settings
          </div>
        )}
        <div
          onClick={props.onClose}
          className="col-start-8 col-span-1 flex items-center justify-center hover:cursor-pointer"
        >
          <CloseIcon />
        </div>
      </div>

      <nav className="w-11/12 h-9/10 flex-grow flex flex-col justify-stretch overflow-y-auto scrollbar-hidden p-1">
        <div className="w-full h-full flex flex-col">
          <Title uppercase={action === undefined}>
            {action?.label ||
              translation[locale]?.configuration ||
              translation["fr-FR"].configuration}
          </Title>
          {action
            ? (action.component as ReactElement)
            : actions.map((action, index) => {
                return (
                  <div
                    onClick={() => setAction(action)}
                    className="w-full py-4 pl-1 border-b border-b-black/25 flex justify-between hover:cursor-pointer"
                    key={index}
                  >
                    {action.label}
                    <RightChevronIcon />
                  </div>
                );
              })}
        </div>
      </nav>
    </div>
  );
};

export default SettingsScreen;
